import { useEffect, useState, useCallback, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

// Perfil simplificado
interface Profile {
  id: string;
  full_name: string | null;
  email: string | null;
  role: string;
}

// Função para recuperar user do sessionStorage
const getStoredUser = (): User | null => {
  try {
    const stored = sessionStorage.getItem('auth_user');
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

// Função para salvar user no sessionStorage
const storeUser = (user: User | null) => {
  try {
    if (user) {
      sessionStorage.setItem('auth_user', JSON.stringify(user));
    } else {
      sessionStorage.removeItem('auth_user');
    }
  } catch (error) {
    console.error('Erro ao salvar user no sessionStorage:', error);
  }
};

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(getStoredUser);
  const [session, setSession] = useState<Session | null>(null); 
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  const syncedUsersRef = useRef(new Set<string>());

  // Função auxiliar para buscar o perfil no banco Supabase
  // Sem dependências para estabilizar a função e prevenir re-renders desnecessários
  const fetchUserProfile = useCallback(async (userId: string) => {
    setProfileLoading(true);
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, name, email, role')
        .eq('id', userId)
        .maybeSingle();
      if (!error && data) {
        setProfile({
          id: data.id,
          full_name: data.name,
          email: data.email,
          role: data.role || 'user',
        });
      } else {
        setProfile({
          id: userId,
          full_name: null,
          email: null,
          role: 'user'
        });
      }
    } finally {
      setProfileLoading(false);
    }
  }, []); // Sem dependências - função estável

  useEffect(() => {
    let mounted = true;
    let isInitializing = true;

    // Listener de mudanças de sessão
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, sessionArg) => {
        if (!mounted) return;

        // Ignorar eventos SIGNED_IN redundantes após inicialização para prevenir re-renders ao trocar de aba
        // IMPORTANTE: Bloquear para TODOS os usuários, incluindo Azure
        const shouldBlock = !isInitializing && 
                           event === 'SIGNED_IN' && 
                           user?.id === sessionArg?.user?.id;
        
        if (shouldBlock) return;

        setSession(sessionArg);
        const newUser = sessionArg?.user ?? null;
        setUser(newUser);
        storeUser(newUser);

        // Se é login com Azure, sincronizar usuário
        if (event === 'SIGNED_IN' && 
            sessionArg?.user?.app_metadata?.provider === 'azure' && 
            !syncedUsersRef.current.has(sessionArg.user.id)) {
          syncedUsersRef.current.add(sessionArg.user.id);

          setTimeout(async () => {
            try {
              await supabase.functions.invoke('azure-user-sync', {
                body: {
                  user: sessionArg.user,
                  action: 'sync_user'
                }
              });
            } catch (error) {
              console.error('Error syncing Azure user:', error);
              syncedUsersRef.current.delete(sessionArg.user.id);
            }
          }, 0);
        }

        if (sessionArg?.user) {
          setTimeout(() => {
            if (mounted) {
              fetchUserProfile(sessionArg.user.id);
            }
          }, 0);
        } else {
          setProfile(null);
        }
        setLoading(false);
      }
    );

    // Buscar sessão inicial
    const initSession = async () => {
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (mounted) {
          setSession(currentSession);
          const initialUser = currentSession?.user ?? null;
          setUser(initialUser);
          storeUser(initialUser);

          if (initialUser) {
            fetchUserProfile(initialUser.id);
          }
        }
      } catch (error) {
        console.error('Error fetching initial session:', error);
      } finally {
        if (mounted) {
          setLoading(false);
          isInitializing = false;
        }
      }
    };

    initSession();

    // Timeout de segurança
    const timeoutId = setTimeout(() => {
      if (mounted) setLoading(false);
    }, 5000);

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchUserProfile]);

  // Função de login
  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        setLoading(false);
        // Verificar se o erro é relacionado a email não confirmado
        if (error.message?.includes('Email not confirmed') || error.message?.includes('email_not_confirmed')) {
          return { error: { ...error, needsEmailConfirmation: true } };
        }
        return { error };
      }
      setSession(data.session);
      const newUser = data.user ?? null;
      setUser(newUser);
      storeUser(newUser);
      if (data.user) {
        await fetchUserProfile(data.user.id);
      }
      return { data, error: null };
    } catch (error) {
      setLoading(false);
      return { error };
    }
  };

  // Função de cadastro
  const signUp = async (email: string, password: string, fullName: string) => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { 
          data: { full_name: fullName },
          emailRedirectTo: `https://app.aroan.com.br/auth/callback`
        }
      });
      
      if (error) {
        setLoading(false);
        return { error };
      }
      
      // Para confirmação de email, o user existe mas session pode ser null
      if (data.user && !data.session) {
        setUser(null);
        storeUser(null);
        setSession(null);
        setProfile(null);
        setLoading(false);
        return { 
          data, 
          error: null, 
          needsEmailConfirmation: true 
        };
      }
      
      setSession(data.session);
      const newUser = data.user ?? null;
      setUser(newUser);
      storeUser(newUser);
      if (data.user) {
        await fetchUserProfile(data.user.id);
      }
      return { data, error: null };
    } catch (error) {
      setLoading(false);
      return { error };
    }
  };

  // Função de logout robusta e sincronização total
  const signOut = async () => {
    setLoading(true);
    
    // Verificar se há uma sessão válida no servidor antes de tentar fazer logout
    try {
      const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();
      
      // Se não há sessão válida no servidor ou houve erro ao verificar, apenas limpar estado local
      if (sessionError || !currentSession) {
        setUser(null);
        storeUser(null);
        setSession(null);
        setProfile(null);
        setLoading(false);
        return { error: null };
      }
    } catch (err) {
      // Se houve erro ao verificar a sessão, apenas limpar estado local
      setUser(null);
      storeUser(null);
      setSession(null);
      setProfile(null);
      setLoading(false);
      return { error: null };
    }

    // Se chegou até aqui, há uma sessão válida - prosseguir com logout normal
    let error = null;
    try {
      const { error: signOutError } = await supabase.auth.signOut();
      if (signOutError) {
        // Verificar se o erro é de sessão não encontrada - isso é esperado e deve ser tratado como sucesso
        if (signOutError.message?.includes('Session from session_id claim in JWT does not exist') || 
            signOutError.message?.includes('session_not_found')) {
          // Tratar como logout bem-sucedido
          error = null;
        } else {
          // Para outros erros, manter o erro original
          error = signOutError;
        }
      }
    } catch (err) {
      // Verificar se o erro capturado também é de sessão não encontrada
      if (err && typeof err === 'object' && 'message' in err) {
        const errMessage = (err as any).message;
        if (errMessage?.includes('Session from session_id claim in JWT does not exist') || 
            errMessage?.includes('session_not_found')) {
          error = null;
        } else {
          error = err;
        }
      } else {
        error = err;
      }
    } finally {
      setUser(null);
      storeUser(null);
      setSession(null);
      setProfile(null);
      syncedUsersRef.current.clear(); // Limpar lista de usuários sincronizados no logout
      setLoading(false);
      setTimeout(() => {
        setUser(null);
        storeUser(null);
        setSession(null);
      }, 0);
    }
    return { error };
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `https://app.aroan.com.br/set-password`;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl
    });
    
    return { error };
  };


  return {
    user,
    session,
    profile,
    loading,
    profileLoading,
    signIn,
    signUp,
    signOut,
    resetPassword
  };
};
